import { useCallback, useContext, useEffect, useState } from 'react';
import type {
  AgGridEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';

import useMetricMatrix from 'hooks/useMetricMatrix';
import buildGridOptions from 'components/Grid/buildGridOptions';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useRowHighlighter from 'hooks/useRowHighlighter';
import useWindowSize from 'hooks/useWindowSize';
import useIsCompatibleComparison from 'hooks/useIsCompatableComparison';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import aguid from 'lib/aguid';
import useApplyMatrixSortChange from './useApplyMatrixSortChange';

// The reversed matrix's pinned Total column, sized by its content. One array
// for every render, so DataTable (memoised) sees the same props.
const REVERSED_AUTO_SIZE_COLUMN_IDS = ['total'];
const useMatrixContent = ({
  chartDefinition,
  gridApi,
  setGridApi,
  isSecondaryRowsStacked,
}: {
  chartDefinition: V5ChartDefinition;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  // Stacked secondary rows (DataTable only): fetch each dimension A value's
  // own row too
  isSecondaryRowsStacked?: boolean;
}): {
  isCompatibleComparison: boolean | undefined;
  gridOptions: GridOptions | undefined;
  columnDefs: ColDef[];
  totals: any;
  gridId: string;
  onDrillDown: (term: string, ctrlKey?: boolean) => void;
  onGridReady: (event: GridReadyEvent) => void;
  rowCount: number;
  noDataToDisplay: boolean;
  isInitialLoading: boolean;
  autoSizeFirstColumnOnly: boolean;
  additionalAutoSizeColumnIds?: string[];
  // For DataTable, which takes its data as props rather than through the
  // grid api
  rowsToDisplay: (MatrixRowData | MatrixComparisonRowData)[];
  // Stacked secondary rows: each dimension A value's own row
  secondaryRowParents?: (MatrixRowData | MatrixComparisonRowData)[];
  isLoading: boolean;
  drillField: string;
} => {
  const { rowHeight } = useContext(RowHeightContext);
  const applyMatrixSortChange = useApplyMatrixSortChange(chartDefinition);
  const isCompatibleComparison = useIsCompatibleComparison(chartDefinition);
  const [gridId] = useState<string>(() => aguid());
  const [def, setDef] = useState<V5ChartDefinition>(chartDefinition);
  const [gridOptions, setGridOptions] = useState<GridOptions>();
  const [currentSortBy, setCurrentSortBy] = useState<
    V5ChartDefinitionSeries | undefined
  >();

  useEffect(() => {
    setCurrentSortBy(def.series.find((s) => s.sortByMetricId));
  }, [def.series]);

  const {
    isLoading,
    isInitialLoading,
    rowsToDisplay,
    secondaryRowParents,
    columnDefs,
    totals,
  } = useMetricMatrix({
    chartDef: def,
    currentSortBy: currentSortBy ? currentSortBy.metricId : undefined,
    isSecondaryRowsStacked,
  });

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption('loading', isLoading);
    }
  }, [gridApi, isLoading]);

  const drillField = (() => {
    if (chartDefinition.dimensionA) {
      if (chartDefinition.dimensionA.field === 'date') {
        return chartDefinition.trendByCalendarInterval || 'day';
      }
      return chartDefinition.dimensionA.field;
    }
    return '';
  })();

  const { onDrillDown } = useChartDrillDowns(
    drillField,
    undefined,
    'matrix',
    chartDefinition,
  );

  const { isMobile } = useWindowSize();

  useRowHighlighter({
    gridApi,
    drillField,
    visId: gridId,
  });

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells();
    }
  }, [drillField, onDrillDown, gridApi]);

  useEffect(() => {
    setDef(chartDefinition);
  }, [chartDefinition, setDef]);

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('rowData', rowsToDisplay);
    }
  }, [gridApi, rowsToDisplay]);

  useEffect(() => {
    setGridOptions({
      ...buildGridOptions({
        columnDefs,
        rowModelType: 'clientSide',
        rowHeight,
      }),
      context: {
        onDrillDown,
      },
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
      },
      suppressCellFocus: true,
      suppressColumnVirtualisation: true,
    });
  }, [columnDefs, onDrillDown, rowHeight]);

  const noDataToDisplay = rowsToDisplay.length === 0;

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);

      event.api.addEventListener('sortChanged', (event: AgGridEvent) => {
        const sortedCol = event.api.getColumnState().find((c) => !!c.sort);
        if (sortedCol && sortedCol.sort) {
          applyMatrixSortChange({
            colId: sortedCol.colId,
            sort: sortedCol.sort,
          });
        }
      });
    },
    [applyMatrixSortChange, setGridApi],
  );

  return {
    isCompatibleComparison,
    gridOptions,
    columnDefs,
    totals,
    gridId,
    onDrillDown,
    onGridReady,
    rowCount: rowsToDisplay.length,
    noDataToDisplay,
    isInitialLoading,
    autoSizeFirstColumnOnly:
      !isMobile && chartDefinition.gridSizing !== 'fitContent',
    // In a reversed matrix the Total column is pinned-right, so it ignores
    // `flex` and would otherwise keep ag-grid's default width. Auto-size it
    // (alongside the first column) so it shrinks to fit its content. Only used
    // in the `autoSizeFirstColumnOnly` (flex) path; Fit-Content already
    // auto-sizes every column.
    additionalAutoSizeColumnIds: chartDefinition.reverseAxes
      ? REVERSED_AUTO_SIZE_COLUMN_IDS
      : undefined,
    rowsToDisplay,
    secondaryRowParents,
    isLoading,
    drillField,
  };
};

export default useMatrixContent;
